import type {LegendFeature, LegendItem} from './LegendItem';

export interface LegendGroupOptions {
  label: string;
  /** An array of legend symbols representing the group's contents. */
  legendSymbols: LegendItem[];
  /** Whether the visibility for the legend group and items can be toggled. */
  legendToggleEnabled?: boolean;
}

/**
 * Represents a group of legend items, allowing toggling visibility for all items in the layer menu and on the map.
 */
export class LegendGroup {
  uid: string;
  label: string;
  legendSymbols: LegendItem[];
  legendToggleEnabled: boolean;

  constructor({
    label,
    legendSymbols,
    legendToggleEnabled,
  }: LegendGroupOptions) {
    this.uid = Math.random().toString(36).substring(2, 15);
    this.label = label;
    this.legendSymbols = legendSymbols;
    this.legendToggleEnabled = legendToggleEnabled ?? true;
  }

  setVisibilityForAll(visible: boolean): void {
    this.legendSymbols.forEach((item) => (item.visible = visible));
  }

  getItemByLabel(label: string): LegendItem | undefined {
    return this.legendSymbols.find((item) => item.label === label);
  }

  toggleItemVisibility(itemId: string | number, visible: boolean): void {
    const item = this.legendSymbols.find((i) => i.uid === itemId);
    if (item) {
      item.visible = visible;
    }
  }

  matchesLegendItem(feature: LegendFeature): boolean {
    return this.legendSymbols.findIndex((item) => item.matches(feature)) > -1;
  }

  isVisible(): boolean {
    return this.legendSymbols.some((item) => item.visible);
  }

  hasInvisibleItems(): boolean {
    return this.legendSymbols.some((item) => !item.visible);
  }
}
